import type { MaterialData } from 'trace-viewer/behaviors';

/**
 * Returns a new materials array with `src` applied at every slot in
 * `targetMaterialIndices`.
 *
 * Takes a list rather than one index because `targetMaterialIndex` is a MESH
 * slot index: meshes that shared one authored glTF material appear as several
 * slots, so a single material choice is several writes (the same reason
 * style adoption fans a decision across `MaterialGroup.slotIndices`). Writing
 * only the slot the caller happened to name leaves the rest of that material
 * un-swapped, which reads as "the tool half worked".
 *
 * Any `baseColorFactor` already on a written slot is dropped: it tinted the
 * material being replaced, and carrying it over would multiply the new
 * albedo by a colour chosen for the old one. UV scale is kept — it describes
 * the surface's texel density, not the material.
 */
export function applyMaterialSrcOverride(
    materials: readonly MaterialData[] | undefined,
    targetMaterialIndices: readonly number[],
    src: string,
): MaterialData[] {
    const out = materials ? [...materials] : [];
    for (const targetMaterialIndex of targetMaterialIndices) {
        const existingIdx = out.findIndex(m => (m.targetMaterialIndex ?? 0) === targetMaterialIndex);
        if (existingIdx >= 0) {
            const { baseColorFactor: _dropped, ...rest } = out[existingIdx];
            out[existingIdx] = { ...rest, src };
        } else {
            out.push({ targetMaterialIndex, src });
        }
    }
    return out;
}
